import React, { useCallback, useState } from 'react';

import Envelope from '@pkg/assets/images/Envelope.png';
import { Checkbox, Modal } from '@pkg/components';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { type OptIn, type OptInType } from '@app/client/common/structs/user/user';
import { updateUserOptIn } from '@app/store/actions/user';

import { CheckboxWrapper, EnvelopeImage, Heading, ModalLayout, StyledForm, SubHeading } from './styles';

export interface EmailPreferencesModalProps {
    onClose(): void;
}

type CurrentOptIns = { [bar in OptInType]: boolean };

export function EmailPreferencesModal({ onClose }: EmailPreferencesModalProps) {
    const dispatch = useAppDispatch();
    const { optIns } = useAppSelector((state) => state.user);

    const checkOptedIn = (option?: OptIn) => {
        return option?.opted_in === null || Boolean(option?.opted_in);
    };

    const getOptInCopy = useCallback(
        (type?: OptInType) => {
            const curr = optIns?.find((optIn) => optIn.opt_in_type === type);
            return curr?.name || '';
        },
        [optIns],
    );

    const getCurrentOptIns = () => {
        return optIns?.reduce<CurrentOptIns>(
            (acc, optIn) => {
                acc[optIn.opt_in_type] = checkOptedIn(optIn);
                return acc;
            },
            {
                FantasyDailyLineupSummary: false,
                FantasyGameInvites: false,
                FantasyGameOverAnnouncements: false,
                MarketingList: false,
            },
        );
    };

    const [userOptIns, setOptIns] = useState(getCurrentOptIns());

    const submitEmailPreferences = useCallback(() => {
        Object.keys(userOptIns || {}).forEach((optInType: string) => {
            dispatch(
                updateUserOptIn({
                    optInType: optInType as OptInType,
                    optInValue: userOptIns[optInType],
                    optInText: getOptInCopy(optInType as OptInType) || '',
                }),
            );
        });
        onClose();
    }, [userOptIns, onClose, dispatch, getOptInCopy]);

    const handleSubmit = useCallback(
        (evt: React.FormEvent) => {
            evt.preventDefault();
            submitEmailPreferences();
        },
        [submitEmailPreferences],
    );

    const handleSetOptIn = (key: OptInType, value: boolean) => {
        setOptIns((curr) => ({ ...curr, [key]: value }));
    };

    return (
        <Modal onClose={onClose}>
            <ModalLayout>
                <StyledForm onSubmit={handleSubmit}>
                    <EnvelopeImage src={Envelope} alt="Envelope image" />
                    <Heading>Update email preferences</Heading>
                    <SubHeading>Choose which emails you’d like to receive.</SubHeading>
                    <CheckboxWrapper>
                        {optIns.map((optIn) => (
                            <Checkbox
                                checked={userOptIns[optIn.opt_in_type]}
                                onChange={(e) => handleSetOptIn(optIn.opt_in_type, e.target.checked)}
                                id={optIn.opt_in_type}
                                key={optIn.opt_in_type}
                            >
                                {optIn.name}
                            </Checkbox>
                        ))}
                    </CheckboxWrapper>
                    <Button size={'large'}>Update</Button>
                </StyledForm>
            </ModalLayout>
        </Modal>
    );
}
